import React, { useEffect } from "react";

interface LoadingAnimationProps {
  timeRemaining: number;
  setTimeRemaining: React.Dispatch<React.SetStateAction<number>>;
  initialTime?: number;
}

const LoadingAnimation = ({ 
  timeRemaining, 
  setTimeRemaining, 
  initialTime = 90 
}: LoadingAnimationProps) => {
  // Start the countdown timer
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, setTimeRemaining]);

  // Calculate progress percentage
  const progressPercentage = ((initialTime - timeRemaining) / initialTime) * 100;

  return (
    <div className="min-h-[500px] flex flex-col items-center justify-center">
      <div className="mb-8 flex flex-col items-center">
        <div className="relative w-40 h-40 mb-8">
          {/* Circular progress background */}
          <div className="absolute inset-0 rounded-full border-8 border-gray-200 dark:border-gray-800"></div>

          {/* Circular progress fill */}
          <svg className="absolute inset-0 w-full h-full rotate-[-90deg]" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="currentColor"
              strokeWidth="8"
              strokeDasharray="289.27"
              strokeDashoffset={289.27 - (289.27 * progressPercentage / 100)}
              className="text-purple-500 dark:text-purple-400"
            />
          </svg>

          {/* Inner pulsing circle */}
          <div className="absolute inset-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {timeRemaining}
            </div>
          </div>

          {/* Pulsing ripples */}
          <div className="absolute inset-0 animate-ping opacity-20 rounded-full bg-purple-500 dark:bg-purple-400" style={{ animationDuration: '3s' }}></div>
          <div className="absolute inset-0 animate-ping opacity-20 rounded-full bg-purple-500 dark:bg-purple-400" style={{ animationDuration: '3s', animationDelay: '1s' }}></div>
        </div>

        <h2 className="text-2xl font-medium text-gray-900 dark:text-gray-100 mb-2">
          Analyzing Your Compliance Process
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
          Our AI consultant is evaluating your data and preparing a comprehensive analysis and optimization plan
        </p>
      </div>

      {/* Animation for the processing stages */}
      <div className="w-full max-w-md">
        <div className="space-y-6">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 0 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 0 ? '✓' : '1'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Analyzing Current Process</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Evaluating efficiency and challenges</div>
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 33 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 33 ? '✓' : '2'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Generating AI Recommendations</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Creating tailored optimization plan</div>
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage > 66 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage > 66 ? '✓' : '3'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Preparing Process Visualization</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Building visual comparison</div>
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${progressPercentage >= 100 ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {progressPercentage >= 100 ? '✓' : '4'}
            </div>
            <div className="ml-4">
              <div className="font-medium">Finalizing Results</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Preparing your complete report</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingAnimation;